import React, { useEffect, useRef } from 'react';

export interface FormTab {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface FormTabsProps {
  tabs: FormTab[];
  active: string;
  onChange: (id: string) => void;
  /** Fülek, amelyeken hibás/hiányzó mező van – piros pöttyöt kapnak */
  errorTabs?: string[];
  /** Rögzítve a modal görgetésekor (a modal belső margóját ellensúlyozza) */
  sticky?: boolean;
}

/**
 * Egységes fülsor űrlapokhoz (ügyfél, beszállító, …).
 * Mobilon görgethető, a hibás fül piros pöttyöt kap.
 */
const FormTabs: React.FC<FormTabsProps> = ({ tabs, active, onChange, errorTabs = [], sticky = true }) => {
  const listRef = useRef<HTMLDivElement>(null);
  // Mobilon (görgethető fülsor) az aktív fül mindig látszódjon
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    el?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [active]);
  return (
  <div className={`${sticky ? 'sticky -top-4 sm:-top-6 z-10' : ''} -mx-4 sm:-mx-6 px-4 sm:px-6 pt-1 bg-[var(--crm-surface)] border-b border-[var(--crm-border)]`}>
    <div ref={listRef} className="flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist">
      {tabs.map(tab => {
        const isActive = active === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.id)}
            className={`relative flex-shrink-0 inline-flex items-center gap-1.5 h-10 px-3 text-[13px] font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
              isActive
                ? 'border-primary-600 text-primary-700 dark:text-primary-300'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
          >
            {tab.icon && <tab.icon className="w-4 h-4" />}
            {tab.label}
            {errorTabs.includes(tab.id) && <span className="w-1.5 h-1.5 rounded-full bg-red-500" aria-label="Hiányzó vagy hibás mező" />}
          </button>
        );
      })}
    </div>
  </div>
  );
};

export default FormTabs;

/** Szekció-kártya fejléccel – űrlapokon belül */
export const FormSection: React.FC<{ title: string; icon?: React.ComponentType<{ className?: string }>; children: React.ReactNode; className?: string }> = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`crm-card ${className}`}>
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
      {Icon && <Icon className="w-4 h-4 text-gray-400" />}
      <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{title}</h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
);
